import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

interface Claim {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  craFinanceLender?: string;
  locSentDate?: string;
  status?: string;
  createdInApiAt?: string;
  daysWithoutLOC?: number;
  lenderFound?: boolean;
  lenderEmail?: string;
  hasLenderEmail?: boolean;
  noLOCReason?: string;
  [key: string]: any;
}

interface ClaimsResponse {
  claims: Claim[];
  count: number;
  totalChecked?: number;
}

const ClaimManagement: React.FC = () => {
  const { apiRequest, userRole } = useAuth();
  
  // State for no LOC claims
  const [noLocClaims, setNoLocClaims] = useState<Claim[]>([]);
  const [noLocCount, setNoLocCount] = useState<number>(0);
  
  // Loading states
  const [noLocLoading, setNoLocLoading] = useState(false);
  const [noLocLoaded, setNoLocLoaded] = useState(false);
  
  const [message, setMessage] = useState('');
  const [editingClaim, setEditingClaim] = useState<Claim | null>(null);
  const [editFormData, setEditFormData] = useState<string>('');

  useEffect(() => {
    // Fetch claims on component mount
    if (!noLocLoaded) {
      fetchNoLocClaims();
    }
  }, [noLocLoaded]);

  const fetchNoLocClaims = async () => {
    setNoLocLoading(true);
    setMessage('');
    try {
      const response = await apiRequest<ClaimsResponse>({
        url: '/ui/claims/no-loc',
        method: 'GET',
      });
      setNoLocClaims(response.claims || []);
      setNoLocCount(response.count || 0);
      setNoLocLoaded(true);
      setMessage(`Found ${response.count} claims without LOC.`);
    } catch (error: any) {
      console.error('Error fetching claims without LOC:', error);
      setMessage('Failed to fetch claims without LOC');
      setNoLocClaims([]);
      setNoLocCount(0);
    } finally {
      setNoLocLoading(false);
    }
  };

  const refreshData = () => {
    setNoLocLoaded(false);
    fetchNoLocClaims();
  };

  const handleEditClick = (claim: Claim) => {
    setEditingClaim(claim);
    // Pretty print the JSON for editing
    const editableData = { ...claim };
    // Remove system fields that shouldn't be edited
    delete editableData._rid;
    delete editableData._self;
    delete editableData._etag;
    delete editableData._attachments;
    delete editableData._ts;
    delete editableData.auditLog;
    setEditFormData(JSON.stringify(editableData, null, 2));
  };

  const handleSaveEdit = async () => {
    if (!editingClaim) return;

    try {
      const updatedData = JSON.parse(editFormData);
      
      // Calculate what changed
      const changes: any = {};
      Object.keys(updatedData).forEach(key => {
        if (updatedData[key] !== editingClaim[key]) {
          changes[key] = updatedData[key];
        }
      });

      if (Object.keys(changes).length === 0) {
        setMessage('No changes detected');
        return;
      }

      const response = await apiRequest({
        url: `/ui/claims/${editingClaim.id}/update`,
        method: 'PUT',
        data: changes,
      });

      setMessage('Claim updated successfully');
      setEditingClaim(null);
      // Refresh to show updated data
      refreshData();
    } catch (error: any) {
      if (error.message && error.message.includes('JSON')) {
        setMessage('Invalid JSON format');
      } else {
        setMessage(error.response?.data?.error || 'Failed to update claim');
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingClaim(null);
    setEditFormData('');
  };

  const isAdmin = userRole === 'admin';

  // Helper function to get reason badge color
  const getReasonBadgeClass = (reason: string) => {
    switch (reason) {
      case 'Ready to send':
        return 'reason-ready';
      case 'Missing lender email':
        return 'reason-email';
      case 'Lender not found in database':
        return 'reason-lender';
      case 'No lender specified':
        return 'reason-no-lender';
      default:
        return 'reason-error';
    }
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="claim-management">
      <div className="header-section">
        <h2>Claims Without LOC</h2>
        <div className="header-stats">
          <span className="stat-badge">{noLocCount} claims pending</span>
        </div>
      </div>
      
      <div className="controls">
        <button onClick={refreshData} className="refresh-btn">
          Refresh Data
        </button>
      </div>

      {message && <div className="message">{message}</div>}

      {noLocLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading claims without LOC...</p>
        </div>
      ) : (
        <div className="claims-table-container">
          <table className="claims-table">
            <thead>
              <tr>
                <th>Claim ID</th>
                <th>Client Name</th>
                <th>Email</th>
                <th>Lender</th>
                <th>Days Waiting</th>
                <th>Created Date</th>
                <th>Reason for No LOC</th>
                <th>Lender Email</th>
                {isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {noLocClaims.map(claim => (
                <tr key={claim.id}>
                  <td>{claim.id}</td>
                  <td>{claim.firstName} {claim.lastName}</td>
                  <td>{claim.email || '-'}</td>
                  <td>{claim.craFinanceLender || '-'}</td>
                  <td>
                    <span className={`days-badge ${claim.daysWithoutLOC && claim.daysWithoutLOC > 7 ? 'days-warning' : ''} ${claim.daysWithoutLOC && claim.daysWithoutLOC > 14 ? 'days-critical' : ''}`}>
                      {claim.daysWithoutLOC !== null ? `${claim.daysWithoutLOC} days` : 'N/A'}
                    </span>
                  </td>
                  <td>{claim.createdInApiAt ? formatDate(claim.createdInApiAt) : 'N/A'}</td>
                  <td>
                    <span className={`reason-badge ${getReasonBadgeClass(claim.noLOCReason || '')}`}>
                      {claim.noLOCReason || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    {claim.hasLenderEmail ? (
                      <span className="email-found">✓ {claim.lenderEmail}</span>
                    ) : (
                      <span className="email-missing">✗ Missing</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td>
                      <button
                        onClick={() => handleEditClick(claim)}
                        className="edit-btn"
                      >
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {noLocClaims.length === 0 && noLocLoaded && (
            <div className="no-claims">
              No claims without LOC found
            </div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {editingClaim && (
        <div className="modal">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>Edit Claim: {editingClaim.id}</h3>
              <span className="close-button" onClick={handleCancelEdit}>&times;</span>
            </div>
            <div className="modal-body">
              <p className="edit-warning">
                ⚠️ Be careful when editing claim data. Changes are logged and cannot be undone.
              </p>
              <textarea
                value={editFormData}
                onChange={(e) => setEditFormData(e.target.value)}
                className="json-editor"
                rows={20}
                cols={80}
              />
            </div>
            <div className="modal-footer">
              <button onClick={handleSaveEdit} className="save-btn">
                Save Changes
              </button>
              <button onClick={handleCancelEdit} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .claim-management {
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .header-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #e0e0e0;
        }

        .header-section h2 {
          margin: 0;
          color: #333;
          font-size: 24px;
        }

        .header-stats {
          display: flex;
          gap: 15px;
        }

        .stat-badge {
          background-color: #f0f8ff;
          color: #007bff;
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 500;
          font-size: 14px;
          border: 1px solid #007bff;
        }

        .controls {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-bottom: 15px;
        }

        .refresh-btn {
          padding: 8px 16px;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s;
          background-color: #28a745;
        }

        .refresh-btn:hover {
          background-color: #218838;
        }


        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: #666;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-container p {
          margin: 0;
          font-size: 16px;
          font-weight: 500;
        }

        .message {
          padding: 10px;
          margin: 10px 0;
          background-color: #f0f8ff;
          border: 1px solid #007bff;
          border-radius: 4px;
          color: #0056b3;
        }

        .claims-table-container {
          overflow-x: auto;
        }

        .claims-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
        }

        .claims-table th,
        .claims-table td {
          border: 1px solid #ddd;
          padding: 12px;
          text-align: left;
        }

        .claims-table th {
          background-color: #f8f9fa;
          font-weight: 600;
        }

        .claims-table tr:nth-child(even) {
          background-color: #f8f9fa;
        }

        .claims-table tr:hover {
          background-color: #e9ecef;
        }

        .days-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          background-color: #e7f3ff;
          color: #0066cc;
        }

        .days-badge.days-warning {
          background-color: #fff3cd;
          color: #856404;
        }

        .days-badge.days-critical {
          background-color: #f8d7da;
          color: #721c24;
        }

        .reason-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .reason-ready {
          background-color: #d4edda;
          color: #155724;
        }

        .reason-email {
          background-color: #fff3cd;
          color: #856404;
        }

        .reason-lender {
          background-color: #f8d7da;
          color: #721c24;
        }

        .reason-no-lender {
          background-color: #e2e3e5;
          color: #383d41;
        }

        .reason-error {
          background-color: #f5c6cb;
          color: #721c24;
        }

        .email-found {
          color: #28a745;
          font-weight: 500;
          font-size: 13px;
        }

        .email-missing {
          color: #dc3545;
          font-weight: 500;
          font-size: 13px;
        }

        .edit-btn {
          padding: 5px 15px;
          background-color: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .edit-btn:hover {
          background-color: #0056b3;
        }

        .no-claims {
          text-align: center;
          padding: 40px;
          color: #666;
          font-style: italic;
        }

        /* Modal Styles */
        .modal {
          position: fixed;
          z-index: 1000;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          overflow: auto;
          background-color: rgba(0,0,0,0.4);
        }

        .modal-content {
          background-color: #fefefe;
          margin: 5% auto;
          padding: 0;
          border: 1px solid #888;
          width: 90%;
          max-width: 800px;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        .modal-content.large {
          max-width: 1000px;
        }

        .modal-header {
          padding: 15px 20px;
          background-color: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-radius: 8px 8px 0 0;
        }

        .modal-body {
          padding: 20px;
        }

        .modal-footer {
          padding: 15px 20px;
          background-color: #f8f9fa;
          border-top: 1px solid #dee2e6;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          border-radius: 0 0 8px 8px;
        }

        .close-button {
          color: #aaa;
          font-size: 28px;
          font-weight: bold;
          cursor: pointer;
          line-height: 20px;
        }

        .close-button:hover,
        .close-button:focus {
          color: black;
          text-decoration: none;
        }

        .edit-warning {
          background-color: #fff3cd;
          border: 1px solid #ffeeba;
          color: #856404;
          padding: 10px;
          border-radius: 4px;
          margin-bottom: 15px;
        }

        .json-editor {
          width: 100%;
          font-family: 'Courier New', monospace;
          font-size: 14px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          padding: 10px;
          background-color: #f8f9fa;
        }

        .save-btn {
          padding: 10px 20px;
          background-color: #28a745;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        }

        .save-btn:hover {
          background-color: #218838;
        }

        .cancel-btn {
          padding: 10px 20px;
          background-color: #6c757d;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        }

        .cancel-btn:hover {
          background-color: #5a6268;
        }
      `}</style>
    </div>
  );
};

export default ClaimManagement; 